import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Form } from 'react-bootstrap';

import { BookingTypeValues } from '@components/BookingType';
import DateRangeDurationPicker, {
  DateRangeDurationPickerProps,
} from '@components/DateRangeDurationPicker';
import DateRangePicker from '@components/DateRangePicker';
import HotelDestinationAutocomplete, {
  HotelDestinationAutocompleteProps,
  parseSuggestDestinationToOption,
} from '@components/HotelDestinationAutocomplete';
import QuantityBox, { QuantityBoxProps } from '@components/QuantityBox';
import { useSearchForm } from '@components/SearchForm';
import Translate from '@components/Translate';
import { roomsFilters } from '@constants';
import useHotelChainSlug from '@hooks/useHotelChainSlug';
import { usePostMessageListener } from '@hooks/usePostMessageListener';
import useTranslate from '@hooks/useTranslate';
import { normalize } from '@spazious/jsonapi-tools';
import { useGetCities, useGetHotels } from '@spazious/react-booking-engine';
import { Divider, FlexBox, Typography } from '@spazious/react-components';
import { IconSearch } from '@spazious/react-icons';
import { styled } from '@spazious/react-styles';
import {
  ExpandedPayload,
  IframeEventName,
  InitConfigPayload,
  PostMessageType,
  ResetCompletedPayload,
  ResetPayload,
} from '@spazious/search-sdk-types';
import postInteractionEvent from '@utils/sdkEvents';

import useGoogleAnalytics from '../GoogleAnalytics/useGoogleAnalytics.js';

import { SearchFormActionKind, SearchFormState } from './SearchFormStore.js';
import { SearchFormBookingType, SearchFormDestinationType } from './SearchFormTypes.js';
import { parseSuggestToDestination } from './SearchFormUtils.js';
import { useSearchFormSubscribe } from './useSearchFormSubscribe.js';

export type SearchFormProps = {
  condensed?: boolean;
  direction?: 'row' | 'column';
};
const StyledSearchContainer = styled(FlexBox)`
  box-shadow: 0 0 1px 1px #efefef;
  border-radius: 0px 12px 12px 12px;
  background-color: var(--background-color);
  width: 100%;

  @media (max-width: 430px) {
    flex-direction: column;
    border-radius: 0 0 12px 12px;
  }
`;

const StyledSearchButton = styled('button')`
  border-radius: 0 12px 12px 0;
  min-width: 50px;
  height: 100%;
  padding: 0;
  background-color: var(--primary-color);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;

  &:hover {
    background-color: var(--primary-color);
    opacity: 0.9;
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.5;
    background-color: var(--primary-color);
    color: #fff;
  }

  @media (max-width: 430px) {
    border-radius: 0 0 12px 12px;
    width: 100%;
    min-height: 48px;
  }
`;

const TabNameCard = styled(FlexBox)`
  display: none;

  @media (max-width: 430px) {
    display: inline-flex; /* Show only on mobile */
    align-items: center;
    justify-content: center;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 22px;
    white-space: nowrap;
    text-align: center;
    color: var(--secondary-color);
  }
`;

export const SearchForm: React.FC<SearchFormProps> = ({ condensed, direction = 'row' }) => {
  const translate = useTranslate();
  const hotelChainSlug = useHotelChainSlug();
  const [
    { destination, dateRange, capacityOrRoomCount, bookingType },
    { setFormData, setDestination, doSearch, setDateRange },
  ] = useSearchForm();

  const [dateRangeValue, setDateRangeValue] = useState<string | null>(dateRange);
  const [capacityValue, setCapacityValue] = useState<number>(capacityOrRoomCount);
  const [temporalDestination, setTemporalDestination] =
    useState<SearchFormState['destination']>(null);

  const [destinationValue, setDestinationValue] =
    useState<PickProp<HotelDestinationAutocompleteProps, 'value'>>(null);
  const [pickerOpen, setPickerOpen] = useState<DateRangeDurationPickerProps['open']>();
  const [pickerControl, setPickerControl] = useState<any>(null);
  const [isIframeExpanded, setIframeExpanded] = useState(false);
  const [pendingPickerOpen, setPendingPickerOpen] = useState(false);
  const [minRooms, setMinRooms] = useState<number>(1);

  usePostMessageListener<InitConfigPayload>(
    PostMessageType.INIT_CONFIG,
    useCallback((payload) => {
      if (typeof payload?.minRooms === 'number') {
        setMinRooms(payload.minRooms);
        setCapacityValue((prev) => Math.max(prev, payload.minRooms!));
      }
    }, [])
  );

  const { search, selectItem } = useGoogleAnalytics();

  const startDate = dateRangeValue?.split('/')[0];
  const endDate = dateRangeValue?.split('/')[1];

  const isDurationValid = dayjs(endDate).diff(dayjs(startDate), 'hours') < 13;

  const commonSearchDisabled =
    !dateRangeValue ||
    dateRangeValue.split('/').filter(Boolean).length < 2 ||
    !capacityValue ||
    !destinationValue;

  const searchDisabled =
    bookingType === BookingTypeValues.OnlySpaces
      ? commonSearchDisabled || !isDurationValid
      : commonSearchDisabled;

  // Fetch hotels and cities
  const { data: hotelsQueryResult, loading: hotelsQueryLoading } = useGetHotels({
    variables: {
      include: ['brand', 'country', 'city'],
      filters: {
        hotelChainSlug: hotelChainSlug,
        ...(bookingType ? roomsFilters[bookingType] : {}),
      },
      pageSize: 100,
    },
    skip: !destination || !hotelChainSlug,
  });

  const hotelsData = useMemo(
    () => (hotelsQueryResult ? normalize(hotelsQueryResult) : []),
    [hotelsQueryResult]
  );

  const { data: citiesQueryResult, loading: citiesQueryLoading } = useGetCities({
    variables: {
      include: ['country'],
      filters: {
        hotelChainSlug: hotelChainSlug,
        ...(bookingType ? roomsFilters[bookingType] : {}),
      },
      pageSize: 100,
    },
    skip: !destination || !hotelChainSlug,
  });

  const citiesData = useMemo(
    () => (citiesQueryResult ? normalize(citiesQueryResult) : []),
    [citiesQueryResult]
  );

  useEffect(() => {
    if (temporalDestination === null && destination) {
      setTemporalDestination({ ...destination });
    }
  }, [destination, temporalDestination]);

  useEffect(() => {
    if (bookingType !== BookingTypeValues.OnlySpaces && dateRange) {
      const start = dateRange.split('/')[0];
      const end = dateRange.split('/')[1];
      if (dayjs(start).date() === dayjs(end).date()) {
        const newRange = start + '/' + dayjs(end).add(1, 'day').format();
        setDateRange(newRange);
        setDateRangeValue(newRange);
      }
    }
  }, [bookingType, dateRange, setDateRange]);

  useEffect(() => {
    if (destination === null) setDestinationValue(null);
    else {
      let foundItem = null;
      if (destination?.type === SearchFormDestinationType.Hotel) {
        foundItem = hotelsData.find(
          (h) => destination.hotelSlug === h?.slug && destination.brandSlug === h?.brand?.slug
        );
      } else if (destination?.type === SearchFormDestinationType.Location) {
        foundItem = citiesData.find(
          (c) => destination.citySlug === c.slug && destination.countrySlug === c.country.slug
        );
      }

      if (foundItem) {
        setDestinationValue(
          parseSuggestDestinationToOption({
            ...foundItem,
            type: destination?.type === SearchFormDestinationType.Hotel ? 'Hotel' : 'Location',
          })
        );
      }
    }
  }, [citiesData, destination, hotelsData]);

  useEffect(() => {
    if (dateRange === null) setDateRangeValue(null);
  }, [dateRange, destination]);

  useSearchFormSubscribe((action) => {
    if (action.type === SearchFormActionKind.DoSearch && searchDisabled) {
      setPickerOpen(true);
    }
  });

  const unitProps = useMemo(
    () => ({
      h: { label: translate('hours'), maxValue: 12 },
      m: { label: translate('minutes') },
    }),
    [translate]
  );

  const handleConfirmSearch = useCallback(() => {
    const formData = {
      dateRange: dateRangeValue,
      capacityOrRoomCount: capacityValue,
      bookingType,
    };

    setFormData(formData);
    search({
      term:
        bookingType === BookingTypeValues.OnlySpaces
          ? SearchFormBookingType.MeetingRoom
          : SearchFormBookingType.GuestRoom,
      ...formData,
    });
    doSearch();
  }, [capacityValue, bookingType, dateRangeValue, doSearch, search, setFormData]);

  const handleSearch = useCallback(
    (evt: React.FormEvent | React.MouseEvent | null) => {
      evt?.preventDefault();
      if (searchDisabled) return;
      setDestination(temporalDestination ?? destination);
      handleConfirmSearch();
    },
    [searchDisabled, handleConfirmSearch, setDestination, temporalDestination, destination]
  );

  const handleDatePickerDurationChange = useCallback((_: any, dateRange: string | null) => {
    setDateRangeValue(dateRange);
  }, []);

  usePostMessageListener<ExpandedPayload>(
    PostMessageType.EXPANDED,
    useCallback(
      (payload: any) => {
        setIframeExpanded(true);

        if (payload?.context === 'PICKER_OPEN' && pendingPickerOpen) {
          setPickerOpen(pickerControl ?? true);
          setPendingPickerOpen(false);
        }
      },
      [pendingPickerOpen, pickerControl]
    )
  );

  const handleReset = useCallback(() => {
    setIframeExpanded(false);
    setPickerOpen(false);
    setPendingPickerOpen(false);
  }, []);
  usePostMessageListener<ResetPayload>(PostMessageType.RESET, handleReset);
  usePostMessageListener<ResetCompletedPayload>(PostMessageType.RESET_COMPLETED, handleReset);

  const handlePickerOpen = useCallback(
    (pickerControl?: any) => {
      if (isIframeExpanded) {
        setPickerOpen(pickerControl ?? true);
      } else {
        // reset related states
        setPickerControl(pickerControl ?? true);
        postInteractionEvent(IframeEventName.PICKER_OPEN);
        setPendingPickerOpen(true);
      }
    },
    [isIframeExpanded]
  );

  const handlePickerClose = useCallback(() => setPickerOpen(false), []);

  const handleDestinationSelect = useCallback<
    PickProp<HotelDestinationAutocompleteProps, 'onSelect'>
  >(
    (_, suggest) => {
      const dest = suggest ? parseSuggestToDestination(suggest) : suggest;
      selectItem({ category: dest?.type, name: dest?.hotelSlug ?? dest?.citySlug });
      setTemporalDestination(dest);
    },
    [selectItem]
  );

  const handleDestinationChange = useCallback<
    PickProp<HotelDestinationAutocompleteProps, 'onChange'>
  >((_, value) => setDestinationValue(value), []);

  const handleAttendesChange = useCallback<PickProp<QuantityBoxProps, 'onChange'>>(
    (evt, attendees) => {
      evt.preventDefault();
      setCapacityValue(attendees);
    },
    []
  );

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent) => {
      if (evt.key === 'Enter' && (evt.target as HTMLElement).tagName !== 'BUTTON') {
        evt.preventDefault();
        handleSearch(evt);
      }
    },
    [handleSearch]
  );

  const getBookingTypeLabel = (value: BookingTypeValues | null): string => {
    switch (value) {
      case BookingTypeValues.OnlyAccommodations:
        return 'Accommodations';
      case BookingTypeValues.OnlySpaces:
        return 'Spaces';
      case BookingTypeValues.AccommodationAndSpaces:
        return 'Accommodations + Spaces';
      default:
        return '';
    }
  };

  return (
    <FlexBox alignItems="center" direction="row" gap={2} justifyContent="center">
      <Form
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
        onKeyDown={handleKeyDown}
        onSubmit={handleSearch}
      >
        <StyledSearchContainer direction="row">
          <FlexBox alignItems="stretch" direction={direction} flex="1" minHeight="60px">
            <TabNameCard>{getBookingTypeLabel(bookingType)}</TabNameCard>
            {/* Destination */}
            <FlexBox
              alignItems="center"
              flex={direction === 'row' ? 1 : 'unset'}
              padding={[0, 2, 0, 1]}
            >
              <HotelDestinationAutocomplete
                isLoading={hotelsQueryLoading || citiesQueryLoading}
                placeholder={translate('Type Destination')}
                value={destinationValue}
                onChange={handleDestinationChange}
                onSelect={handleDestinationSelect}
              />
            </FlexBox>

            <Divider
              flexItem
              orientation={direction === 'column' ? 'horizontal' : 'vertical'}
              style={{ borderTopColor: 'var(--background-muted)' }}
            />

            {/* Date Picker */}
            <FlexBox
              flex={
                direction === 'row'
                  ? bookingType === BookingTypeValues.OnlySpaces
                    ? 2
                    : 1
                  : 'unset'
              }
              justifyContent="center"
            >
              {bookingType === BookingTypeValues.OnlySpaces ? (
                <DateRangeDurationPicker
                  borderless
                  name="dateRange"
                  open={pickerOpen}
                  unitsProps={unitProps}
                  value={dateRangeValue}
                  onChange={handleDatePickerDurationChange}
                  onClose={handlePickerClose}
                  onOpen={handlePickerOpen}
                />
              ) : (
                <DateRangePicker
                  borderless
                  name="dateRange"
                  open={!!pickerOpen}
                  placeholderText={`${translate('Check-in')} - ${translate('Check-out')}`}
                  value={dateRangeValue}
                  onChange={handleDatePickerDurationChange}
                  onClose={handlePickerClose}
                  onOpen={handlePickerOpen}
                />
              )}
            </FlexBox>
            <Divider
              flexItem
              orientation={direction === 'column' ? 'horizontal' : 'vertical'}
              style={{ borderTopColor: 'var(--background-muted)' }}
            />
            <FlexBox
              alignItems="center"
              flex={direction === 'row' ? 1 : 'unset'}
              justifyContent="center"
              style={{ minWidth: direction === 'row' ? '80px' : '100%' }}
            >
              <FlexBox
                alignItems="center"
                flex="1"
                justifyContent="center"
                style={{ position: 'relative', minHeight: '60px' }}
              >
                <QuantityBox
                  inputWidth={condensed ? '40px' : '80px'}
                  minValue={minRooms}
                  name="attendees"
                  value={capacityValue}
                  onChange={handleAttendesChange}
                />
                {!condensed && (
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: 'var(--secondary-text)',
                      backgroundColor: 'var(--background-color)',
                      padding: '0 5px',
                      position: 'absolute',
                      top: '0px',
                    }}
                    variant="caption"
                  >
                    {bookingType === BookingTypeValues.OnlySpaces ? (
                      <Translate>Capacity</Translate>
                    ) : (
                      <Translate>Rooms</Translate>
                    )}
                  </Typography>
                )}
              </FlexBox>
            </FlexBox>
          </FlexBox>
          {/* Search Button */}
          <FlexBox>
            <StyledSearchButton disabled={searchDisabled} onClick={handleSearch}>
              <IconSearch />
            </StyledSearchButton>
          </FlexBox>
        </StyledSearchContainer>
      </Form>
    </FlexBox>
  );
};

export default SearchForm;
